import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { User } from 'configcat-react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import TagManager from 'react-gtm-module';
import { OpenLoanStatus } from '../clients/LoanClient';
import { AppSettingsContext } from '../contexts/AppSettingsContext';
import { InitDatadogLogs, InitDatadogRum } from './Datadog/InitDatadog';
import { ApplicationDecision } from './Enums/ApplicationDecision';
import { ApplicationStatus } from './Enums/ApplicationStatus';
import { appendMember, getPage, getPageKeyBasedOnOpenLoanStatus } from './Helpers/ApplicationStatusHelpers';
import { getFriendlyOpenLoanStatus } from '../utils/Helpers/OpenLoanStatusEnum';
import useAccountClient from '../hooks/account/Client';
import { hashDataUsingSHA256 } from '../utils/Helpers/EncryptionHelper';
import { initBraze, trackBrazeUser } from './Braze/InitBraze';
import { ClassicCard } from '@braze/web-sdk';
import { AddressDetails, BankDetails, EmploymentDetails } from '../clients/AccountClient';

const existingCustomerKey = 'ec';
const isPersonalDetailsCompleteKey = 'isPersonalDetailsComplete';
const customerDetailsKey = 'customerDetails';
const concurrentLoanEligibilityKey = 'cle';
const isLimpSubscriberKey = 'limpSubscriber'

export interface TrackingProps {
  children: React.ReactNode;
}

export interface FeatureFlagEvent {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  featureFlag: any;
  value: boolean | string | number | unknown;
}

export interface CustomerTrackingDetails {
  accountId?: string,
  email?: string,
  name?: string,
  existingCustomer?: boolean
}

export interface CustomConfigCatAttributes {
  isMobile: string,
  browser: string,
  device: string
}


const defaultCustomerTrackingDetails: CustomerTrackingDetails = {
  accountId: '',
  email: '',
  name: '',
  existingCustomer: false,
};

const defaultConfigCatUser: User = {
  identifier: '',
  country: '',
  email: '',
  custom: {

  }
}

export interface InsuranceType {
  segment: string,
  deathBenefitChosen: boolean,
}

export const TrackingContext = React.createContext({
  /* eslint-disable  @typescript-eslint/no-unused-vars*/
  TrackUserId: (accountId: string) => {
    /* empty */
  },
  TrackUserEmail: (email?: string) => {
    /* empty */
  },
  TrackBankName: (bankName?: string) => {
    /* empty */
  },
  PageTrack: (name: string) => {
    /* empty */
  },
  /* eslint-disable  @typescript-eslint/no-explicit-any*/

  EventTrack: (eventName: string, properties?: any) => {
    /* empty */
  },
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  TrackFeatureFlagEvent: ({ featureFlag, value }: FeatureFlagEvent) => { },
  TrackCustomerDetails: (customer: CustomerTrackingDetails) => {
    /* empty */
  },
  TrackQuotation: (loanDetails: any, loanId: string) => {
    /* empty */
  },
  TrackConversion: async (isConcurrentLoan: boolean) => {
    /* empty */
  },
  ApplicationDecisionTrack: (applicationDecision: ApplicationDecision, isConcurrentLoan: boolean) => {
    /* empty */
  },
  ApplicationStatusTrack: (applicationStatus: ApplicationStatus, isConcurrentLoan: boolean) => {
    /* empty */
  },
  isExistingCustomer: false,
  SetExistingCustomer: (isExistingCustomer: boolean) => {
    /* do nothing */
  },
  isPersonalDetailsComplete: false,
  SetIsPersonalDetailsComplete: (isPersonalDetailsComplete: boolean) => {
    /* do nothing */
  },
  TrackError: (error: unknown, errorMessage?: string) => {
    /* empty */
  },
  TrackInfo: (message: string, error?: unknown) => {
    /* empty */
  },
  TrackWarning: (message: string, error?: unknown) => {
    /* empty */
  },
  GetAccountSummaryPageKey: (isConcurrentLoan: boolean, openLoanStatus?: OpenLoanStatus): string => {
    return '';
  },
  TrackAction: (actionName: string, value: object) => {
    /* empty */
  },
  customerTrackingDetails: defaultCustomerTrackingDetails,
  configCatUserDetails: defaultConfigCatUser,
  isConcurrentLoanEligible: false,
  SetIsConcurrentLoanEligible: (isConcurrentEligible: boolean) => {
    /* do nothing */
  },
  contentCards: [] as ClassicCard[],
  setContentCards: (cards: ClassicCard[]) => {
    /* do nothing */
  },
  TrackAddressValues: (address: AddressDetails | undefined) => {
    /* do nothing */
  },
  TrackEmploymentValues: (employment: EmploymentDetails | undefined) => {
    /* do nothing */
  },
  TrackBankValues: (bank: BankDetails | undefined) => {
    /* do nothing */
  },
  TrackInsurance: (insuranceType: "DeathBenefit" | "CreditLife") => {
    /* do nothing */
  },
  isLimpSubscriber: false,
  SetIsLimpSubscriber: (isExistingCustomer: boolean) => {
    /* do nothing */
  },
});

export const useTracking = () => {
  return useContext(TrackingContext);
};

const TrackingContextProvider: React.FC<TrackingProps> = ({ children }) => {
  const appSettings = useContext(AppSettingsContext);

  const [isExistingCustomer, setIsExistingCustomer] = useState(() => {
    const storedValue = localStorage.getItem(existingCustomerKey);
    return storedValue ? JSON.parse(storedValue) : false;
  });

  const [isPersonalDetailsComplete, setIsPersonalDetailsComplete] = useState<boolean>(() => {
    const storedValue = localStorage.getItem(isPersonalDetailsCompleteKey.toString());
    return storedValue ? JSON.parse(storedValue) : false;
  });

  const [customerTrackingDetails, setCustomerTrackingDetails] = useState<CustomerTrackingDetails>(() => {
    const storedValue = localStorage.getItem(customerDetailsKey.toString());
    return storedValue ? JSON.parse(storedValue) : defaultCustomerTrackingDetails;
  });

  const [isConcurrentLoanEligible, setIsConcurrentLoanEligible] = useState(() => {
    const storedValue = localStorage.getItem(concurrentLoanEligibilityKey);
    return storedValue ? JSON.parse(storedValue) : false;
  });

  const [isLimpSubscriber, setIsLimpSubscriber] = useState(() => {
    const storedValue = localStorage.getItem(isLimpSubscriberKey);
    return storedValue ? JSON.parse(storedValue) : false;
  });

  const [configCatUserDetails, setConfigCatUserDetails] = useState<User>(defaultConfigCatUser);

  const accountClient = useAccountClient();
  const [contentCards, setContentCards] = useState<ClassicCard[]>([]);
  const trackedUserRef = useRef<{ id: string; email?: string }>({ id: '' });

  useEffect(() => {
    mapConfigCatUser(customerTrackingDetails);
  }, [customerTrackingDetails]);

  const mapConfigCatUser = (customerTrackingDetails: CustomerTrackingDetails) => {

    setConfigCatUserDetails({
      identifier: customerTrackingDetails.accountId || '',
      country: '',
      email: customerTrackingDetails.email,
      custom: {
        ec: String(customerTrackingDetails.existingCustomer)
      }
    })
  }

  useEffect(() => {
    InitDatadogRum(appSettings);
    InitDatadogLogs(appSettings);
    initBraze(appSettings, (cards: ClassicCard[]) => setContentCards(cards));

    return () => {
      datadogRum.stopSession();
    };
  }, [appSettings]);

  useEffect(() => {
    const tagManagerArgs = {
      gtmId: appSettings.googleTagManager.gtmId,
    };

    TagManager.initialize(tagManagerArgs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const googleTagManager = (name: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'content-view',
        pagePath: name,
        pageTitle: name,
      },
    });
  };

  const datadogRumTracking = (name: string) => {
    if (appSettings.datadogConfig.datadogEnabled) {
      datadogRum.startView(name.replace(/^\/+/, ''));
    }
  };

  const TrackPageVisit = (name: string) => {
    //Marketing notice /undefined being tracked not sure where this came from so adding an additional check
    if (name && name != '/undefined') {
      googleTagManager(name);
      datadogRumTracking(name);
    }
  };

  const TrackEvent = (eventName: string, properties?: any) => {
    if (appSettings.datadogConfig.datadogEnabled) {
      datadogRum.addAction(eventName, { ...properties });
    }
  };

  const trackFeatureFlagEvent = ({ featureFlag, value }: FeatureFlagEvent) => {
    if (!appSettings.datadogConfig.datadogEnabled) return;

    datadogRum.addFeatureFlagEvaluation(featureFlag, value);
    datadogLogs.logger.info('Feature flag evaluated', {
      featureFlag,
      value: value ?? 'undefined'
    });
  };

  const TrackUserId = (accountId: string) => {
    trackedUserRef.current = {
      ...trackedUserRef.current,
      id: accountId
    };

    if (appSettings.datadogConfig.datadogEnabled) {
      datadogRum.setGlobalContextProperty('AccountId', accountId);
      datadogLogs.setGlobalContextProperty('AccountId', accountId);

      datadogRum.setUser(trackedUserRef.current);
      datadogLogs.setUser(trackedUserRef.current);
    }

    TagManager.dataLayer({
      dataLayer: {
        event: 'interaction',
        AccId: accountId,
      },
    });

    trackBrazeUser(accountId);
  };

  const TrackUserEmail = (email = '') => {
    if (!trackedUserRef.current.id) return;
    if (!email) return;

    trackedUserRef.current = {
      ...trackedUserRef.current,
      email
    };

    if (appSettings.datadogConfig.datadogEnabled) {
      datadogRum.setUser(trackedUserRef.current);
      datadogLogs.setUser(trackedUserRef.current);
    }
  };

  const TrackBankName = (bankName = '') => {
    if (!trackedUserRef.current.id) return;
    if (!bankName) return;

    if (appSettings.datadogConfig.datadogEnabled) {
      datadogRum.setAccount({
        id: trackedUserRef.current.id,
        name: bankName
      });
      datadogLogs.setAccount({
        id: trackedUserRef.current.id,
        name: bankName
      });
    }
  }

  const TrackCustomerDetails = (customertrackingDetails: CustomerTrackingDetails) => {
    const currentDetails = JSON.parse(localStorage.getItem(customerDetailsKey) || '{}');
    const updatedUserDetails = {
      ...currentDetails,
      ...Object.fromEntries(
        Object.entries(customertrackingDetails).filter(([_, value]) => value !== undefined && value !== "")
      )
    };

    localStorage.setItem(customerDetailsKey, JSON.stringify(updatedUserDetails));
    setCustomerTrackingDetails(updatedUserDetails);
  };

  const setQuotationValues = (quotationResponse: any, loanId: string) => {
    if (appSettings.datadogConfig.datadogEnabled) {
      datadogRum.setGlobalContextProperty('loanDetails', quotationResponse);
      datadogRum.setGlobalContextProperty('LoanId', loanId);

      datadogLogs.setGlobalContextProperty('loanDetails', quotationResponse);
      datadogLogs.setGlobalContextProperty('LoanId', loanId);
    }

    TagManager.dataLayer({
      dataLayer: {
        event: 'interaction',
        ...quotationResponse,
        LoanId: loanId,
      },
    });
  };

  const trackAddressValues = (address: AddressDetails | undefined) => {
    if (appSettings.datadogConfig.datadogEnabled) {
      const filteredAddress = {
        suburb: address?.suburb,
        town: address?.town,
        province: address?.district,
        postalCode: address?.postCode
      }

      datadogRum.setGlobalContextProperty('addressDetails', filteredAddress);
      datadogLogs.setGlobalContextProperty('addressDetails', filteredAddress);
    }
  };

  const trackEmploymentValues = (employment: EmploymentDetails | undefined) => {
    if (appSettings.datadogConfig.datadogEnabled) {
      const filteredEmployment = {
        employerName: employment?.employerName ?? null,
        employmentStatus: employment?.employmentStatusEnum,
        grossIncome: employment?.grossIncome,
        netIncome: employment?.netIncome,
        salaryDay: employment?.salaryDay,
      }

      datadogRum.setGlobalContextProperty('employmentDetails', filteredEmployment);
      datadogLogs.setGlobalContextProperty('employmentDetails', filteredEmployment);
    }
  };

  const trackBankValues = (bank: BankDetails | undefined) => {
    if (appSettings.datadogConfig.datadogEnabled) {
      const filteredBank = {
        bankName: bank?.bankName
      }
      datadogRum.setGlobalContextProperty('bankDetails', filteredBank);
      datadogLogs.setGlobalContextProperty('bankDetails', filteredBank);
    }
  };

  const trackConversion = async (isConcurrentLoan: boolean) => {
    try {
      if (appSettings.googleTagManager.enhancedTrackingEnabled) {
        const personalDetails = await accountClient.getPersonalDetails();

        const hashedPhoneNumber = await hashDataUsingSHA256(personalDetails.personalDetails?.contact?.mobileNumber?.replace('0', '+27') ?? "");
        const hashedFirstName = await hashDataUsingSHA256(personalDetails.personalDetails?.customer?.firstName?.toLowerCase() ?? "");
        const hashedLastName = await hashDataUsingSHA256(personalDetails.personalDetails?.customer?.lastName?.toLowerCase() ?? "");
        const hashedEmailAddress = await hashDataUsingSHA256(personalDetails.personalDetails?.contact?.email?.toLowerCase() ?? "");

        TagManager.dataLayer({
          dataLayer: {
            event: 'conversion',
            conv_customer: isConcurrentLoan ? 'ec-plus' : isExistingCustomer ? 'ec' : 'nc',
          }
        });

        TagManager.dataLayer({
          dataLayer: {
            event: 'userdata',
            user_provided_data: {
              email_address: hashedEmailAddress,
              phone_number: hashedPhoneNumber,
              address: {
                first_name: hashedFirstName,
                last_name: hashedLastName,
                street: personalDetails.personalDetails?.address?.street?.toLowerCase(),
                city: personalDetails.personalDetails?.address?.town?.toLowerCase(),
                region: personalDetails.personalDetails?.address?.district?.toLowerCase(),
                postal_code: personalDetails.personalDetails?.address?.postCode,
                country: "south africa"
              }
            }
          },
        });
      }
    } catch (error) {
      datadogLogs.logger.info('Could not log GTM user information');
    }
  };

  const trackError = (error: unknown, errorMessage?: string) => {
    datadogRum.addError(error);

    if (error instanceof Error) {
      datadogLogs.logger.error(errorMessage ?? 'Error occurred', {}, error);
    } else if (typeof error === 'string') {
      datadogLogs.logger.error(errorMessage ?? 'Error occurred', {}, new Error(error));
    }
  };

  const trackInfo = (message: string, error?: unknown) => {
    if (!error) {
      datadogLogs.logger.info(message, {});
    } else if (error instanceof Error) {
      datadogLogs.logger.info(message, {}, error);
    } else if (typeof error === 'string') {
      datadogLogs.logger.error(message, {}, new Error(error));
    }
  };

  const trackWarning = (message: string, error?: unknown) => {
    if (!error) {
      datadogLogs.logger.warn(message, {});
    } else if (error instanceof Error) {
      datadogLogs.logger.warn(message, {}, error);
    } else if (typeof error === 'string') {
      datadogLogs.logger.warn(message, {}, new Error(error));
    }
  };

  const trackAction = (actionName: string, value: object) => {
    datadogRum.addAction(actionName, value);
  }

  const TrackApplicationDecision = (applicationDecision: ApplicationDecision, isConcurrentLoan: boolean) => {
    switch (applicationDecision) {
      case ApplicationDecision.ReadyToSign:
        TrackApplicationStatus(ApplicationStatus.readyToSign, isConcurrentLoan);
        break;
      case ApplicationDecision.Accepted:
        TrackApplicationStatus(ApplicationStatus.thanks, isConcurrentLoan);
        break;
      case ApplicationDecision.Declined:
        TrackApplicationStatus(ApplicationStatus.declined, isConcurrentLoan);
        break;
      case ApplicationDecision.CounterOffered:
        TrackApplicationStatus(ApplicationStatus.counterOffer, isConcurrentLoan);
        break;
      default:
        TrackApplicationStatus(ApplicationStatus.timeout, isConcurrentLoan);
    }
  };

  const TrackApplicationStatus = (applicationStatus: ApplicationStatus, isConcurrentLoan: boolean): void => {
    const pageMember = appendMember(getPage(applicationStatus), isExistingCustomer, isConcurrentLoan);
    TrackPageVisit(pageMember);
  };

  const getAccountSummaryPageKey = (isConcurrentLoan: boolean, openLoanStatus?: OpenLoanStatus) => {
    if (openLoanStatus === undefined || openLoanStatus === null) return '';

    const friendlyOpenLoanStatus = getFriendlyOpenLoanStatus(openLoanStatus);
    const pageKey = getPageKeyBasedOnOpenLoanStatus(friendlyOpenLoanStatus);
    if (!pageKey) return '';
    return appendMember(pageKey, isExistingCustomer, isConcurrentLoan);
  };

  const trackInsurance = (insuranceType: "DeathBenefit" | "CreditLife") => {
    if (appSettings.datadogConfig.datadogEnabled) {

      datadogRum.setGlobalContextProperty('insuranceSegment', insuranceType);
      datadogLogs.setGlobalContextProperty('insuranceSegment', insuranceType);
    }
  }

  const setLimpSubscriber = (value: boolean) => {
    setIsLimpSubscriber(value);
    localStorage.setItem(isLimpSubscriberKey, JSON.stringify(value));
  }

  /* We set whether the customer is an existing customer in auth context when they are logged in */
  function SetExistingCustomer(value: boolean) {
    TrackCustomerDetails({ existingCustomer: value })
    setIsExistingCustomer(value);
    localStorage.setItem(existingCustomerKey, JSON.stringify(value));
  }

  function SetIsConcurrentLoanEligible(value: boolean) {
    setIsConcurrentLoanEligible(value);
    localStorage.setItem(concurrentLoanEligibilityKey, JSON.stringify(value));
  }

  function SetIsPersonalDetailsComplete(value: boolean) {
    setIsPersonalDetailsComplete(value);
    localStorage.setItem(isPersonalDetailsCompleteKey, JSON.stringify(value));
  }

  const values = {
    TrackUserId,
    TrackUserEmail,
    TrackBankName,
    PageTrack: TrackPageVisit,
    EventTrack: TrackEvent,
    TrackCustomerDetails,
    TrackQuotation: setQuotationValues,
    TrackConversion: trackConversion,
    ApplicationDecisionTrack: TrackApplicationDecision,
    ApplicationStatusTrack: TrackApplicationStatus,
    isExistingCustomer,
    SetExistingCustomer,
    isPersonalDetailsComplete,
    SetIsPersonalDetailsComplete,
    TrackError: trackError,
    GetAccountSummaryPageKey: getAccountSummaryPageKey,
    TrackFeatureFlagEvent: trackFeatureFlagEvent,
    TrackInfo: trackInfo,
    TrackWarning: trackWarning,
    TrackAction: trackAction,
    customerTrackingDetails,
    configCatUserDetails,
    isConcurrentLoanEligible,
    SetIsConcurrentLoanEligible,
    contentCards: contentCards,
    setContentCards: setContentCards,
    TrackAddressValues: trackAddressValues,
    TrackEmploymentValues: trackEmploymentValues,
    TrackBankValues: trackBankValues,
    TrackInsurance: trackInsurance,
    isLimpSubscriber,
    SetIsLimpSubscriber: setLimpSubscriber,
  };

  return (
    <>
      <TrackingContext.Provider value={values}>{children}</TrackingContext.Provider>
    </>
  );
};

export default TrackingContextProvider;
